import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import GoogleMapReact from 'google-map-react';

import { setPostLoginRedirectPath } from 'auth/postLoginRedirect';
import {
  canAccessAISpeedToLead,
  canAccessComingSoonFeatures,
  canAccessCompanyAIOverview,
  canViewPricingData,
  hasCompanyAIOverviewFlag,
  isMysteryShopAdmin,
  isMysteryShopShopper,
} from 'helpers/permissions';
import { useBeamer } from 'hooks/useBeamer';
import { useAppSelector } from 'store/hook';

import AppLayout from '.';
import { consumeSupportModeRedirectPath } from './hooks';

const PrivateRoot = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const DEFAULT_PATHS = ['/'];
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const { selectedCommunity, loading, communities } = useAppSelector((state) => state.global);
  const showPricingData = canViewPricingData(currentUser);

  // Initialize Beamer for the current user
  useBeamer(currentUser);

  useEffect(() => {
    if (!currentUser) {
      setPostLoginRedirectPath(location.pathname + location.search);
      navigate('/sign-in');
    } else if (currentUser.pending_password_reset && !currentUser.manager) {
      // Temporary measure intended to enforce stronger password for all users
      navigate('/forgot-password');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const queuedRedirectPath = consumeSupportModeRedirectPath();
    if (queuedRedirectPath && location.pathname !== queuedRedirectPath) {
      navigate(queuedRedirectPath);
    }
  }, [currentUser?.user_id, location.pathname, navigate]);

  useEffect(() => {
    if (DEFAULT_PATHS.includes(location.pathname) && currentUser && !currentUser.isSuperuser) {
      // Without the AI Company Overview flag this must keep the pre-AI-Overview homepage behavior.
      const hasCompanyAIOverviewHomepage = hasCompanyAIOverviewFlag(currentUser);
      const isLoadingCommunities = !selectedCommunity?.id && loading;
      // The AI Overview decision only needs the community count, so an already loaded list is enough to proceed.
      const canDecideAIOverviewHomepage = hasCompanyAIOverviewHomepage && communities.length > 0;
      if (isLoadingCommunities && !canDecideAIOverviewHomepage) {
        return;
      }

      const canUseAICompetitiveIntelligence =
        Boolean(currentUser?.feature_flags?.show_ai_competitive_intelligence) && !currentUser?.isSuperuser;
      const canUseCompanyAIOverview = canAccessCompanyAIOverview(currentUser, communities.length);
      const canUseSpeedToLead = hasCompanyAIOverviewHomepage && canAccessAISpeedToLead(currentUser);
      const canUseComingSoonFeatures = canAccessComingSoonFeatures(currentUser);

      if (isMysteryShopAdmin(currentUser) || isMysteryShopShopper(currentUser)) {
        navigate('/mystery-shopping');
      } else if (selectedCommunity?.id) {
        navigate(`/community/${selectedCommunity.id}`);
      } else if (canUseCompanyAIOverview) {
        navigate('/ai-overview');
      } else if (
        !showPricingData &&
        (canUseAICompetitiveIntelligence || canUseSpeedToLead || canUseComingSoonFeatures)
      ) {
        navigate('/ai-competitive-intelligence');
      } else {
        navigate('/overview');
      }
    } else if (DEFAULT_PATHS.includes(location.pathname) && currentUser && currentUser.isSuperuser) {
      navigate('/companies');
    }
  }, [
    location.pathname,
    currentUser?.user_id,
    currentUser?.isSuperuser,
    selectedCommunity?.id,
    loading,
    showPricingData,
    communities.length,
    currentUser?.feature_flags?.show_ai_company_overview,
    navigate,
  ]);

  return (
    <AppLayout>
      <GoogleMapReact
        style={{ display: 'none' }}
        bootstrapURLKeys={{
          key: 'AIzaSyAkEDUPfo1e_5N59IClF74jjicyKbTMYw4',
          libraries: ['places'],
        }}
        defaultCenter={{ lat: 40.7, lng: -74.0 }}
        defaultZoom={10}
      />
      <Outlet />
    </AppLayout>
  );
};

export default PrivateRoot;
